import { useHoldRepeat } from '@src/hooks/useHoldRepeat';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { formatTimecode, parseTimecode, wholeFps } from '@src/utility/timecode';
import React, { useRef } from 'react';
import styled from 'styled-components';

import { fieldFocusCss } from '../../transcript/translationEditorPrimitives';

type Props = {
  /**
   * Raw text the user is editing. Held by the parent so Save can validate and
   * commit alongside the body in one action.
   */
  value: string;
  onChange: (value: string) => void;
  framerate: number;
  /** Clip length; the field refuses a time past the end of the clip. */
  duration: number;
  /** Drop the pin, turning this back into a general comment. */
  onUnpin: () => void;
  /** Snap the field to the playhead — the "it's right here" shortcut. */
  onUsePlayhead: () => void;
  disabled?: boolean;
};

/**
 * Timecode editor for a pinned clip comment (PLAT-10951). Dragging the scrubber
 * marker covers "roughly later"; this covers "exactly here" — when the reviewer
 * already knows the frame and does not want to hunt for it.
 *
 * A condensed sibling of the transcript quick-edit popover's timing field: same
 * `m:ss:ff` parser (now shared via `@src/utility/timecode`), same frame
 * steppers, same focus ring. It is narrower because a comment carries one time,
 * not a start/end pair.
 */
export const CommentTimecodeField: React.FC<Props> = ({
  value,
  onChange,
  framerate,
  duration,
  onUnpin,
  onUsePlayhead,
  disabled = false,
}) => {
  const { start, stop } = useHoldRepeat();
  const parsed = parseTimecode(value, framerate);
  const invalid = parsed === null || (duration > 0 && parsed > duration);

  // `useHoldRepeat` re-invokes the SAME callback on an interval, so `step` must
  // read the live value rather than the one its closure captured — otherwise
  // every tick recomputes from the value at mouse-down and a held stepper never
  // moves past the first frame.
  const valueRef = useRef(value);
  valueRef.current = value;

  const step = (frames: number) => {
    const current = parseTimecode(valueRef.current, framerate);
    if (current === null) return;
    const next = current + frames / wholeFps(framerate);
    const clamped = Math.max(0, duration > 0 ? Math.min(duration, next) : next);
    onChange(formatTimecode(clamped, framerate));
  };

  return (
    <Wrapper>
      <Row>
        <InputGroup>
          <Input
            type="text"
            inputMode="numeric"
            aria-label="Comment timecode"
            aria-invalid={invalid}
            data-testid="comment-timecode-input"
            placeholder="0:00:00"
            value={value}
            disabled={disabled}
            $invalid={invalid}
            onChange={e => onChange(e.target.value)}
            // Escape/Enter are handled by the sibling body textarea's handler;
            // stopping here would break Save-on-Enter for the whole edit form.
          />
          {/* tabIndex -1: Tab should move to the body, not through two nudge
              buttons the keyboard can already replicate by typing. */}
          <Steppers>
            <StepperButton
              type="button"
              tabIndex={-1}
              aria-label="Nudge one frame later"
              disabled={disabled}
              onMouseDown={() => start(() => step(1))}
              onMouseUp={stop}
              onMouseLeave={stop}
            >
              <UpGlyph>
                <ChevronDownIcon width="10" height="10" />
              </UpGlyph>
            </StepperButton>
            <StepperButton
              type="button"
              tabIndex={-1}
              aria-label="Nudge one frame earlier"
              disabled={disabled}
              onMouseDown={() => start(() => step(-1))}
              onMouseUp={stop}
              onMouseLeave={stop}
            >
              <ChevronDownIcon width="10" height="10" />
            </StepperButton>
          </Steppers>
        </InputGroup>
        <InlineAction
          type="button"
          data-testid="comment-timecode-use-playhead"
          disabled={disabled}
          onClick={onUsePlayhead}
        >
          Use Playhead
        </InlineAction>
        <InlineAction
          type="button"
          data-testid="comment-timecode-unpin"
          disabled={disabled}
          onClick={onUnpin}
        >
          Unpin
        </InlineAction>
      </Row>
      {invalid && (
        <FieldError data-testid="comment-timecode-error">
          {parsed !== null && duration > 0 && parsed > duration
            ? 'That moment is past the end of the clip.'
            : 'Enter a time as m:ss or m:ss:frames.'}
        </FieldError>
      )}
    </Wrapper>
  );
};

export default CommentTimecodeField;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
`;

const Row = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

const InputGroup = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
  /* Wide enough for "10:00:00" and no wider — the body textarea is the field
     that deserves the room. */
  width: 108px;
`;

const Input = styled.input<{ $invalid?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  padding: 4px 6px;
  font-size: var(--text-xs, 12px);
  font-variant-numeric: tabular-nums;
  color: var(--foreground);
  background: var(--background);
  border: 1px solid
    ${props => (props.$invalid ? 'var(--destructive)' : 'var(--border)')};
  border-radius: var(--radius-sm, 4px);
  box-sizing: border-box;

  ${fieldFocusCss}
`;

const Steppers = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
`;

const StepperButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  flex: 1 1 0;
  padding: 0;
  line-height: 1;
  cursor: pointer;
  color: var(--text-muted);
  background: var(--background);
  border: 1px solid var(--border);

  &:first-child {
    border-radius: var(--radius-sm, 4px) var(--radius-sm, 4px) 0 0;
    border-bottom: none;
  }
  &:last-child {
    border-radius: 0 0 var(--radius-sm, 4px) var(--radius-sm, 4px);
  }
  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// The icon set has one chevron; flip it rather than adding a near-duplicate
// file (see the "no new icon when one rotates" rule in the design system).
const UpGlyph = styled.span`
  display: inline-flex;
  transform: rotate(180deg);
`;

// Text-styled buttons, matching the list's existing Cancel affordance so the
// edit form doesn't sprout competing button weights.
const InlineAction = styled.button`
  padding: 0 2px;
  font-size: var(--text-xs, 12px);
  color: var(--text-muted);
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: var(--foreground);
    text-decoration: underline;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const FieldError = styled.span`
  color: var(--destructive);
  font-size: var(--text-xs, 12px);
  line-height: 1.3;
`;
